import type { ViewData } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/multisection.data';
import { getMultiSectionFigmaLayout } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/sections.common';

import type {
  BrandConcessionContentSectionDTO,
  BrandSectionTemplateType,
} from '~gateways/ownable.dtos';

type SectionTypeGapRelated =
  | 'Header'
  | 'HeroAndText'
  | 'HeroLeftRight'
  | 'HeroNoText'
  | 'Merchandiser'
  | 'NoHeroText'
  | 'NotSet'
  | 'ProductShelf'
  | 'ThreeUp';

const TYPICAL_GAP = 120;

/** Defines the gap between two sections based on their type.
 * The first key is the type of the previous section, the second key is the type of the next section.
 * https://app.clickup.com/t/863gya2r7
 */
export const GAP_DEFINITIONS: Record<
  SectionTypeGapRelated,
  Record<SectionTypeGapRelated, number>
> = {
  Header: {
    Header: 0,
    ProductShelf: TYPICAL_GAP * 0.83333, // 100 when typical is 120
    Merchandiser: TYPICAL_GAP,
    HeroAndText: TYPICAL_GAP,
    HeroLeftRight: TYPICAL_GAP,
    HeroNoText: TYPICAL_GAP,
    NoHeroText: TYPICAL_GAP,
    ThreeUp: 0,
    NotSet: 0,
  },
  ProductShelf: {
    Header: 0,
    ProductShelf: 0,
    Merchandiser: TYPICAL_GAP,
    HeroLeftRight: TYPICAL_GAP,
    HeroAndText: TYPICAL_GAP,
    HeroNoText: TYPICAL_GAP,
    NoHeroText: TYPICAL_GAP,
    ThreeUp: 0,
    NotSet: 0,
  },
  Merchandiser: {
    Header: 0,
    ProductShelf: TYPICAL_GAP,
    Merchandiser: 0,
    HeroAndText: 0,
    HeroNoText: 0,
    NoHeroText: TYPICAL_GAP,
    HeroLeftRight: TYPICAL_GAP,
    ThreeUp: 0,
    NotSet: 0,
  },
  HeroAndText: {
    Header: 0,
    ProductShelf: 0,
    Merchandiser: 0,
    HeroAndText: TYPICAL_GAP,
    HeroNoText: TYPICAL_GAP,
    NoHeroText: TYPICAL_GAP,
    HeroLeftRight: TYPICAL_GAP,
    ThreeUp: 0,
    NotSet: 0,
  },
  HeroNoText: {
    Header: 0,
    ProductShelf: TYPICAL_GAP * 1.25, // 150 when typical is 120
    Merchandiser: 0,
    HeroAndText: 0,
    HeroNoText: 0,
    NoHeroText: TYPICAL_GAP,
    HeroLeftRight: TYPICAL_GAP,
    ThreeUp: 0,
    NotSet: 0,
  },
  NoHeroText: {
    Header: 0,
    ProductShelf: TYPICAL_GAP,
    Merchandiser: TYPICAL_GAP,
    HeroAndText: TYPICAL_GAP,
    HeroNoText: TYPICAL_GAP,
    NoHeroText: TYPICAL_GAP,
    HeroLeftRight: TYPICAL_GAP,
    ThreeUp: TYPICAL_GAP,
    NotSet: 0,
  },
  HeroLeftRight: {
    Header: 0,
    ProductShelf: TYPICAL_GAP,
    Merchandiser: TYPICAL_GAP,
    HeroAndText: TYPICAL_GAP,
    HeroNoText: TYPICAL_GAP,
    NoHeroText: TYPICAL_GAP,
    HeroLeftRight: TYPICAL_GAP,
    ThreeUp: TYPICAL_GAP,
    NotSet: 0,
  },
  ThreeUp: {
    Header: 0,
    ProductShelf: 0,
    Merchandiser: 0,
    HeroAndText: 0,
    HeroNoText: 0,
    NoHeroText: 0,
    HeroLeftRight: 0,
    ThreeUp: 0,
    NotSet: 0,
  },
  NotSet: {
    Header: 0,
    HeroLeftRight: 0,
    Merchandiser: 0,
    HeroAndText: 0,
    HeroNoText: 0,
    NoHeroText: 0,
    ThreeUp: 0,
    ProductShelf: 0,
    NotSet: 0,
  },
};

export function getPreviousSection(
  viewData: ViewData,
  currentSection: BrandConcessionContentSectionDTO
): BrandConcessionContentSectionDTO | undefined {
  const sectionIndex = viewData.modelData.sections!.findIndex(
    (item) => item.id === currentSection.id
  );

  if (sectionIndex === -1) {
    throw new Error(
      `Section with id ${currentSection.id} not found, when building gaps between sections`
    );
  }

  if (sectionIndex === 0) {
    return undefined;
  }

  return viewData.modelData.sections![sectionIndex - 1];
}

export function getNextSection(
  viewData: ViewData,
  currentSection: BrandConcessionContentSectionDTO
): BrandConcessionContentSectionDTO | undefined {
  const sectionIndex = viewData.modelData.sections!.findIndex(
    (item) => item.id === currentSection.id
  );

  if (sectionIndex === -1) {
    throw new Error(
      `Section with id ${currentSection.id} not found, when building gaps between sections`
    );
  }

  return viewData.modelData.sections![sectionIndex + 1];
}

function getPreviousSectionGapRelatedType(
  viewData: ViewData,
  currentSection: BrandConcessionContentSectionDTO
): SectionTypeGapRelated {
  const previousSection = getPreviousSection(viewData, currentSection);

  return getGapRelatedType(previousSection ?? { template: 'Header' });
}

function getNextSectionGapRelatedType(
  viewData: ViewData,
  currentSection: BrandConcessionContentSectionDTO
): SectionTypeGapRelated {
  const nextSection = getNextSection(viewData, currentSection);

  return getGapRelatedType(nextSection ?? { template: 'NotSet' });
}

export function getGapHeightWithPreviousSection(
  viewData: ViewData,
  section: BrandConcessionContentSectionDTO
): number {
  const previousType = getPreviousSectionGapRelatedType(viewData, section);
  const currentType = getGapRelatedType(section);

  const gap = GAP_DEFINITIONS[previousType][currentType];

  const scaleFactor =
    getMultiSectionFigmaLayout(viewData) === 'full' ? 1 : 0.35; // mobile is 0.35 of desktop

  return gap * scaleFactor;
}

export function getGapHeightWithNextSection(
  viewData: ViewData,
  section: BrandConcessionContentSectionDTO
): number {
  const nextType = getNextSectionGapRelatedType(viewData, section);
  const currentType = getGapRelatedType(section);

  const gap = GAP_DEFINITIONS[nextType][currentType];

  const scaleFactor =
    getMultiSectionFigmaLayout(viewData) === 'full' ? 1 : 0.35; // mobile is 0.35 of desktop

  return gap * scaleFactor;
}

export function getGapRelatedType({
  template,
  heroImageUrl,
  text,
}: {
  template: BrandSectionTemplateType;
  heroImageUrl?: string;
  text?: string;
}): SectionTypeGapRelated {
  if (template === 'HeroLeft' || template === 'HeroRight') {
    return 'HeroLeftRight';
  }

  if (template.includes('Merchandiser')) {
    return 'Merchandiser';
  }

  if (template === 'HeroAndText') {
    if (heroImageUrl && text) {
      return 'HeroAndText';
    }

    if (!heroImageUrl) {
      return 'NoHeroText';
    }

    return 'HeroNoText';
  }

  return template as SectionTypeGapRelated;
}
